import { createFileRoute, Link } from "@tanstack/react-router";
import { CreditCard, HelpCircle, Library, MessageSquare, UserRound } from "lucide-react";

import { Header } from "@/components/Header";
import { SupportRequestForm } from "@/components/SupportRequestForm";

export const Route = createFileRoute("/support")({
  head: () => ({
    meta: [
      { title: "Support — Real Pranks" },
      {
        name: "description",
        content:
          "Get help with your Real Pranks membership, billing, video access, and account.",
      },
      { property: "og:title", content: "Support — Real Pranks" },
      {
        property: "og:description",
        content: "Get help with your Real Pranks membership and account.",
      },
    ],
  }),
  component: SupportPage,
});

const TOPICS = [
  {
    icon: CreditCard,
    title: "Billing & payments",
    body: "Questions about charges, receipts, or updating your payment method? Visit your account to manage your subscription, or email us with your account email.",
  },
  {
    icon: Library,
    title: "Video access & library",
    body: "Your library grows each week while you're subscribed. If a video isn't showing up, try signing out and back in, or use the recheck option on your account page.",
  },
  {
    icon: MessageSquare,
    title: "Drop notifications",
    body: "Enable email or SMS alerts from your library page or account settings. SMS drop alerts are available for US and Canada phone numbers.",
  },
  {
    icon: UserRound,
    title: "Account & login",
    body: "Reset your password from the login page, or update your profile details from your account settings after signing in.",
  },
  {
    icon: HelpCircle,
    title: "Cancel or change plans",
    body: "You can upgrade, downgrade, or cancel anytime from your account. Your library progress may reset if you fully cancel and resubscribe later.",
  },
];

function SupportPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="mx-auto max-w-4xl px-4 py-12 md:py-16">
        <span className="text-[10px] font-bold tracking-[0.25em] text-muted-foreground uppercase">
          Support
        </span>
        <h1 className="mt-2 text-3xl font-bold md:text-4xl">Support</h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
          Need help with your membership, videos, or account? Most answers are
          below. Submit a question and we&apos;ll try to answer right away.
        </p>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {TOPICS.map((t) => (
            <div
              key={t.title}
              className="rounded-2xl border border-border bg-card p-6"
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-foreground">
                <t.icon className="h-4 w-4" />
              </span>
              <h2 className="mt-4 text-lg font-semibold">{t.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{t.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <SupportRequestForm />
        </div>

        <p className="mt-8 text-xs text-muted-foreground">
          Already signed in?{" "}
          <Link to="/account" className="underline underline-offset-4">
            Visit your account
          </Link>
          .
        </p>
      </main>
    </div>
  );
}
